import { downloadList } from './list-export';
import { NumberedPager } from '@/components/list/NumberedPager';
import { ListExportButton, useListExportActions } from '@/components/list/ListExportButton';
import { useNarrow } from '@/components/list/use-narrow';
import type { ListQuery } from './list-query';
import { EXPENSE_CATEGORIES } from '@console/shared-kernel';
import { Car, Check, ChevronDown, Plus, Receipt, SearchX } from 'lucide-react';
import { type ReactNode, useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { PageHead } from '@/admin-area/ui/PageHead';
import { PhonePrimary } from '@/admin-area/ui/PhonePrimary';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import type { FilterField } from '@/components/list/filters';
import { ClickableRow } from '@/components/list/ClickableRow';
import { ListToolbar } from '@/components/list/ListToolbar';
import { COMPACT_ONLY, LIST_CONTAINER, WIDE_ONLY } from '@/components/list/list-columns';
import { useListParams } from '@/components/list/use-list-params';
import type { ListView } from '@/components/list/ViewTabs';
import { Button } from '@/components/ui/button';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ExportExpensesButton, useExpensesExportActions } from '../exports/ExportButtons';
import { ImportReferenceLink, useImportAction } from '../import/ImportReferenceLink';
import { useMileageActions } from '../mileage/MileageLinks';
import { formatAmount, subtractAmounts } from '../money';
import type { CardNavigation } from '../detail/card-navigation';
import {
  categoryLabel,
  DOCUMENT_STATUSES,
  DocumentBadge,
  EXPENSE_STATUSES,
  formatShortDate,
  PAYERS,
  StatusBadge,
  UntraceableBadge,
} from './expense-labels';
import {
  ALL_PERIODS,
  buildListQuery,
  FILTER_KEYS,
  type FilterKey,
  monthLabel,
  monthOf,
} from './list-query';
import { useHiddenExpenses } from './hidden-expenses';
import { systemViews } from './system-views';
import { expenseName } from './row-title';
import { useExpensePeople } from './use-expense-people';
import { type ExpenseRow, useExpensesList } from './use-expenses-list';
import {
  ClassCell,
  ClassHeader,
  classifies,
  filtersByClass,
  useClassFilterField,
} from './ClassColumn';
import { useExpensesPermissions } from '../use-expenses-permissions';

/**
 * «Note spese»: the expenses the caller may see (design §1.1, §1.2; the board's «Elenco»). The
 * shared filterable list (decision 101): the supplier search, the filters as labels, «Mostra
 * altri», the total of every matching expense. A table from 620 px of shell width, cards below.
 * The period starts at the current month; removing its label shows every period.
 */
export function ExpensesListPage() {
  const { t, i18n } = useTranslation('expenses');
  const { t: tl } = useTranslation('expenses-list');
  const titleId = useId();
  const { active, q, pageSize, setPageSize, set, clear, page, setPage, sort } = useListParams(
    FILTER_KEYS,
    true,
  );
  const { t: tc } = useTranslation('common');
  const [field, direction] = sort.split(':');
  const sortField =
    (['date', 'total', 'supplier', 'status'] as const).find((value) => value === field) ?? 'date';
  const sortDirection = direction === 'asc' ? 'asc' : 'desc';
  const ownerId = useAuth().user?.uid ?? null;
  const [today] = useState(() => new Date());
  const currentMonth = monthOf(today);
  const chosenPeriod = active['period'] ?? null;
  const period = chosenPeriodOf(chosenPeriod, currentMonth);

  const { state, reload, ownerAllowed, query } = useOwnerAwareList(
    active,
    period,
    q,
    pageSize,
    (page - 1) * pageSize,
    sortField,
    sortDirection,
  );
  const people = useExpensePeople(ownerAllowed);
  const location = useLocation();

  const { shown, shownTotal } = useShownRows(state);
  // What the card's ‹ › walk through, and where its «Note spese» link returns.
  const navigation = useMemo<CardNavigation>(
    () => ({ ids: shown.map((row) => row.id), from: `${location.pathname}${location.search}` }),
    [shown, location.pathname, location.search],
  );

  const language = i18n.resolvedLanguage;
  const expensesPermissions = useExpensesPermissions();
  const classifier = classifies(expensesPermissions);
  const classFilter = filtersByClass(expensesPermissions);
  const classField = useClassFilterField();
  const baseFields = useFilterFields(state, people, language);
  const fields = useMemo(
    () => (classFilter ? [...baseFields, classField] : baseFields),
    [classFilter, baseFields, classField],
  );

  const onFilter = useCallback(
    (key: string, value: string | null) =>
      // Removing the period's label means every period, not the default month again.
      set(key, key === 'period' && value === null ? ALL_PERIODS : value),
    [set],
  );
  const onSearch = useCallback((value: string) => set('q', value), [set]);
  const toolbarActive = { ...active, period };
  const views = useListViews({ active, period, currentMonth, ownerId, ownerAllowed });

  const narrowed = isNarrowed(q, active, ownerAllowed);
  const canCreate = state.status === 'ready' ? state.can.create : false;
  const showOwner = state.status === 'ready' && state.can.filterByOwner;

  const newExpense = <NewExpenseButton />;
  const offset = (page - 1) * pageSize;
  const summary =
    state.status === 'ready' && shown.length > 0 ? (
      <ListSummary
        from={offset + 1}
        to={offset + shown.length}
        count={state.total - state.items.length + shown.length}
        total={shownTotal}
      />
    ) : undefined;

  // The phone (#956): import, the exports and «Nuovo viaggio» fold into the toolbar's «···».
  const narrow = useNarrow();
  const owner = ownerAllowed ? (active['owner'] ?? null) : null;
  const internalClass = classFilter ? (active['class'] ?? null) : null;
  const exportActions = useListExportActions((format) =>
    downloadList('/v1/expenses/export', query, format),
  );
  const more = [
    ...useImportAction(),
    ...useExpensesExportActions(period, owner, internalClass),
    ...exportActions,
    ...useMileageActions(canCreate),
  ];

  return (
    <section
      aria-labelledby={titleId}
      // The FAB floats over the end of the list: room for it, so the last card is never under it.
      className={cn(
        'flex max-w-full min-w-0 flex-col gap-4',
        canCreate && '@max-[619px]/shell:pb-20',
      )}
    >
      <PageHead
        titleId={titleId}
        title={t('list.title')}
        summary={showOwner ? t('list.summaryAll') : t('list.summaryOwn')}
        actions={
          <>
            {narrow ? null : (
              <>
                <ImportReferenceLink />
                {/* #952 M5: every export on the title row, each saying what it holds. */}
                <ListExportButton
                  onExport={(format) => downloadList('/v1/expenses/export', query, format)}
                  scope={
                    state.status === 'ready' ? tl('exportScope', { count: state.total }) : undefined
                  }
                />
                {/* #915 F4: the expenses file for the list's month, class and owner. */}
                <ExportExpensesButton period={period} owner={owner} internalClass={internalClass} />
              </>
            )}
            {canCreate ? (
              <span className="page-primary">
                <NewExpenseSplit />
              </span>
            ) : null}
          </>
        }
      />
      <ListToolbar
        label={t('list.title')}
        sort={{
          label: tc('list.sort'),
          value: `${sortField}:${sortDirection}`,
          onChange: (value) => set('sort', value),
          options: (['date', 'total', 'supplier', 'status'] as const).flatMap((field) =>
            (['asc', 'desc'] as const).map((direction) => ({
              value: `${field}:${direction}`,
              label: `${t(`list.columns.${field}`)} · ${tc(direction === 'asc' ? 'list.ascending' : 'list.descending')}`,
            })),
          ),
        }}
        search={{ label: t('list.search'), value: q, onWrite: onSearch }}
        fields={fields}
        active={toolbarActive}
        onFilter={onFilter}
        onClear={clear}
        more={more}
        views={{
          label: tl('views.label'),
          items: views.items,
          current: views.current,
          onSelect: views.onSelect,
        }}
        scope={
          ownerAllowed && ownerId !== null ? (
            <ScopeSwitch ownerId={ownerId} owner={active['owner'] ?? null} onChoose={onFilter} />
          ) : undefined
        }
      />
      <ListBody
        state={state}
        shown={shown}
        narrowed={narrowed}
        period={period}
        language={language}
        showOwner={showOwner}
        showClass={classifier}
        navigation={navigation}
        newExpense={canCreate ? newExpense : undefined}
        onClear={clear}
        onReload={reload}
      />
      {state.status === 'ready' ? (
        <NumberedPager
          page={page}
          pageSize={pageSize}
          total={state.total}
          onPage={setPage}
          summary={summary}
          sizeChoice={{ value: pageSize, onChange: setPageSize }}
        />
      ) : null}
      {canCreate ? (
        <PhonePrimary>
          <Button asChild>
            <Link to="/expenses/new">
              <Plus />
              {t('list.new')}
            </Link>
          </Button>
        </PhonePrimary>
      ) : null}
    </section>
  );
}

/**
 * System views (U6): presets of the filters; the active tab follows the address, so a copied link
 * reopens the same view. The period counts as «all» when the address names none of it.
 */
function useListViews({
  active,
  period,
  currentMonth,
  ownerId,
  ownerAllowed,
}: {
  readonly active: Record<string, string | null | undefined>;
  readonly period: string | null;
  readonly currentMonth: string;
  readonly ownerId: string | null;
  readonly ownerAllowed: boolean;
}) {
  const { t: tl } = useTranslation('expenses-list');
  const items = useMemo(
    () =>
      systemViews(
        {
          mine: tl('views.mine'),
          noDocument: tl('views.noDocument'),
          companyCard: tl('views.companyCard'),
          refused: tl('views.refused'),
        },
        { currentMonth, ownerId: ownerAllowed ? ownerId : null },
      ),
    [tl, currentMonth, ownerId, ownerAllowed],
  );
  const current = useMemo(
    () => ({
      get: (key: string) => (key === 'period' ? (period ?? ALL_PERIODS) : (active[key] ?? null)),
    }),
    [active, period],
  );
  // One write for the whole preset: router updates do not queue, so one `set` per key would keep
  // only the last. The search and the order stay; every filter the view does not name is cleared.
  const [, setSearchParams] = useSearchParams();
  const onSelect = useCallback(
    (view: ListView) =>
      setSearchParams(
        (existing) => {
          const next = new URLSearchParams(existing);
          next.delete('page');
          for (const key of FILTER_KEYS) {
            const value = view.params[key];
            if (value === undefined) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  return { items, current, onSelect };
}

function NewExpenseButton() {
  const { t } = useTranslation('expenses');
  return (
    <Button asChild>
      <Link to="/expenses/new">
        <Plus />
        {t('list.new')}
      </Link>
    </Button>
  );
}

/**
 * «Nuova spesa ▾» (#952 M7): the page's one primary, with the other ways to add one behind its
 * chevron. «Nuovo viaggio» is one of them: an action, so it is no longer drawn as a selected tab.
 */
function NewExpenseSplit() {
  const { t } = useTranslation('expenses');
  const { t: tl } = useTranslation('expenses-list');
  const navigate = useNavigate();
  return (
    <span className="inline-flex items-stretch">
      <Button asChild className="rounded-r-none">
        <Link to="/expenses/new">
          <Plus />
          {t('list.new')}
        </Link>
      </Button>
      <RowActionsMenu
        label={tl('newMore')}
        triggerTitle={tl('newMore')}
        triggerIcon={ChevronDown}
        triggerClassName="bg-primary text-primary-foreground hover:bg-primary-hover hover:text-primary-foreground border-primary-foreground/30 h-auto min-h-[38px] w-9 self-stretch rounded-l-none border-l"
        actions={[
          {
            id: 'trip',
            label: t('mileage.links.newTrip'),
            icon: Car,
            onSelect: () => void navigate('/expenses/trips/new'),
          },
        ]}
      />
    </span>
  );
}

/** Anything beyond the period narrows the list: then an empty answer is «no results». */
function isNarrowed(
  q: string,
  active: Record<string, string | null | undefined>,
  ownerAllowed: boolean,
): boolean {
  return (
    q !== '' ||
    FILTER_KEYS.some(
      (key) =>
        key !== 'period' && (active[key] ?? null) !== null && (key !== 'owner' || ownerAllowed),
    )
  );
}

/** The owner filter is sent only once the server has said the caller may use it. */
function useOwnerAwareList(
  active: Record<string, string | null | undefined>,
  period: string | null,
  q: string,
  pageSize: number,
  offset: number,
  sort: ListQuery['sort'],
  direction: ListQuery['direction'],
) {
  const [ownerAllowed, setOwnerAllowed] = useState(false);
  const query = useMemo(
    () =>
      buildListQuery(
        { ...(active as Record<FilterKey, string | null>), period, q },
        { canFilterByOwner: ownerAllowed, pageSize, offset, sort, direction },
      ),
    [active, period, q, ownerAllowed, pageSize, offset, sort, direction],
  );
  const { state, reload } = useExpensesList(query);
  if (state.status === 'ready' && state.can.filterByOwner !== ownerAllowed) {
    setOwnerAllowed(state.can.filterByOwner);
  }
  return { state, reload, ownerAllowed, query };
}

type ListState = ReturnType<typeof useExpensesList>['state'];

function chosenPeriodOf(chosen: string | null, currentMonth: string): string | null {
  if (chosen === null) return currentMonth;
  return chosen === ALL_PERIODS ? null : chosen;
}

/** A draft deleted from its card leaves the list at once, while its undo window runs. */
function useShownRows(state: ListState) {
  const hidden = useHiddenExpenses();
  const ready = state.status === 'ready' ? state : null;
  const shown = useMemo(
    () => (ready === null ? [] : ready.items.filter((row) => !hidden.has(row.id))),
    [ready, hidden],
  );
  const shownTotal = useMemo(() => {
    if (ready === null) return '0.00';
    const gone = ready.items.filter((row) => hidden.has(row.id)).map((row) => row.total);
    return gone.length === 0 ? ready.totalAmount : subtractAmounts(ready.totalAmount, gone);
  }, [ready, hidden]);
  return { shown, shownTotal };
}

function useFilterFields(
  state: ListState,
  people: ReturnType<typeof useExpensePeople>,
  language: string | undefined,
): FilterField[] {
  const { t } = useTranslation('expenses');
  const { t: tl } = useTranslation('expenses-list');
  return useMemo<FilterField[]>(() => {
    const out: FilterField[] = [
      {
        key: 'inReport',
        label: tl('inReport'),
        options: [{ value: 'false', label: tl('unreported') }],
      },
      {
        key: 'period',
        label: t('list.filters.period'),
        monthPicker: true,
        options: [],
      },
      {
        key: 'status',
        label: t('list.filters.status'),
        options: EXPENSE_STATUSES.map((value) => ({ value, label: t(`list.status.${value}`) })),
      },
      {
        key: 'category',
        label: t('list.filters.category'),
        options: EXPENSE_CATEGORIES.map((category) => ({
          value: category.code,
          label: categoryLabel(category.code, language),
        })),
      },
      {
        key: 'payer',
        label: t('list.filters.payer'),
        options: PAYERS.map((value) => ({ value, label: t(`list.payer.${value}`) })),
      },
      {
        key: 'document',
        label: t('list.filters.document'),
        options: DOCUMENT_STATUSES.map((value) => ({ value, label: t(`list.document.${value}`) })),
      },
    ];
    if (state.status === 'ready' && state.can.filterByOwner) {
      out.push({
        key: 'owner',
        label: t('list.filters.owner'),
        options: (people ?? state.owners).map((owner) => ({
          value: owner.userId,
          label: owner.displayName,
        })),
      });
    }
    return out;
  }, [t, tl, language, state, people]);
}

function EmptyList({
  narrowed,
  period,
  language,
  newExpense,
  onClear,
}: {
  readonly narrowed: boolean;
  readonly period: string | null;
  readonly language: string | undefined;
  readonly newExpense: ReactNode;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation('expenses');
  if (narrowed) {
    return (
      <StateMessage
        icon={SearchX}
        title={t('list.noResults.title')}
        text={t('list.noResults.text')}
        action={
          <Button variant="outline" onClick={onClear}>
            {t('list.noResults.clear')}
          </Button>
        }
      />
    );
  }
  return (
    <StateMessage
      icon={Receipt}
      title={
        period === null
          ? t('list.empty.titleAll')
          : t('list.empty.title', { period: monthLabel(period, language) })
      }
      text={t('list.empty.text')}
      action={newExpense}
    />
  );
}

/**
 * The list's one count line (#952 M6): which rows of how many, and the total of every result, not
 * only of this page («totale dei risultati»).
 */
function ListSummary({
  from,
  to,
  count,
  total,
}: {
  readonly from: number;
  readonly to: number;
  readonly count: number;
  readonly total: string;
}) {
  const { t } = useTranslation('expenses');
  return (
    <span data-testid="expenses-footer" className="text-muted-foreground">
      {t('list.footer.range', { from, to, count, total: formatAmount(total) })}
    </span>
  );
}

function ListBody({
  state,
  shown,
  narrowed,
  period,
  language,
  showOwner,
  showClass,
  navigation,
  newExpense,
  onClear,
  onReload,
}: {
  readonly state: ListState;
  readonly shown: readonly ExpenseRow[];
  readonly narrowed: boolean;
  readonly period: string | null;
  readonly language: string | undefined;
  readonly showOwner: boolean;
  readonly showClass: boolean;
  readonly navigation: CardNavigation;
  readonly newExpense: ReactNode;
  readonly onClear: () => void;
  readonly onReload: () => void;
}) {
  const { t } = useTranslation('expenses');
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('list.loading')} className="flex flex-col gap-2">
        {[0, 1, 2, 3, 4].map((n) => (
          <Skeleton key={n} className="h-12 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (state.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={Receipt}
        title={t('list.loadError')}
        action={
          <Button variant="outline" onClick={onReload}>
            {t('list.retry')}
          </Button>
        }
      />
    );
  }
  if (shown.length === 0) {
    return (
      <EmptyList
        narrowed={narrowed}
        period={period}
        language={language}
        newExpense={newExpense}
        onClear={onClear}
      />
    );
  }
  return (
    <>
      <ExpensesTable
        items={shown}
        showOwner={showOwner}
        showClass={showClass}
        navigation={navigation}
      />
      <ExpenseCards items={shown} navigation={navigation} showOwner={showOwner} />
    </>
  );
}

const HEAD = 'px-3 py-2.5 font-semibold';

/**
 * Below 1180 px of the list's own width (#952 H7, U8: `list-columns.ts`) the table keeps Data,
 * Fornitore (with the person and the type under it), Totale and Stato (with a missing document in
 * it); «Cl.» is on the expense's own page.
 */
function ExpensesTable({
  items,
  showOwner,
  showClass,
  navigation,
}: {
  readonly items: readonly ExpenseRow[];
  readonly showOwner: boolean;
  readonly showClass: boolean;
  readonly navigation: CardNavigation;
}) {
  const { t, i18n } = useTranslation('expenses');
  const navigate = useNavigate();
  return (
    <div
      className={cn(
        'border-border hidden overflow-x-auto rounded-xl border @min-[620px]/shell:block',
        LIST_CONTAINER,
      )}
    >
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t('list.title')}</caption>
        <thead className="bg-muted/50 text-muted-foreground text-left text-xs tracking-wide uppercase">
          <tr>
            <th scope="col" className={cn(HEAD, 'w-px whitespace-nowrap')}>
              {t('list.columns.date')}
            </th>
            {showOwner ? (
              <th scope="col" className={cn(HEAD, 'w-px whitespace-nowrap', WIDE_ONLY)}>
                {t('list.columns.owner')}
              </th>
            ) : null}
            <th scope="col" className={HEAD}>
              {t('list.columns.supplier')}
            </th>
            <th scope="col" className={cn(HEAD, WIDE_ONLY)}>
              {t('list.columns.category')}
            </th>
            <th scope="col" className={cn(HEAD, 'w-px text-right whitespace-nowrap')}>
              {t('list.columns.total')}
            </th>
            <th scope="col" className={cn(HEAD, 'w-px whitespace-nowrap', WIDE_ONLY)}>
              {t('list.columns.document')}
            </th>
            <th scope="col" className={cn(HEAD, 'w-px whitespace-nowrap')}>
              {t('list.columns.status')}
            </th>
            {showClass ? <ClassHeader className={WIDE_ONLY} /> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const category = categoryLabel(row.category, i18n.resolvedLanguage);
            return (
              <ClickableRow
                key={row.id}
                className="border-border border-t"
                onOpen={() =>
                  void navigate(`/expenses/${encodeURIComponent(row.id)}`, { state: navigation })
                }
              >
                <td className="px-3 py-3 whitespace-nowrap tabular-nums">
                  {formatShortDate(row.date)}
                </td>
                {showOwner ? (
                  <td className={cn('px-3 py-3 whitespace-nowrap', WIDE_ONLY)}>
                    {row.owner.displayName}
                  </td>
                ) : null}
                <td className="px-3 py-3">
                  {/* The supplier is plain text (U1): it names the supplier, not the expense. */}
                  <span className="text-foreground font-semibold">{expenseName(row, t)}</span>
                  {/* The row click is a mouse convenience; keyboard and screen-reader users get a
                    link named after the expense it opens, shown when it takes the focus. */}
                  <Link
                    to={`/expenses/${encodeURIComponent(row.id)}`}
                    state={navigation}
                    className="focus-visible:bg-background sr-only rounded underline focus-visible:not-sr-only focus-visible:ml-2 focus-visible:px-1"
                  >
                    {t('list.openExpense', { name: expenseName(row, t) })}
                  </Link>
                  {/* The tablet's folded columns: the person and the type under the supplier. */}
                  <span className={cn('text-muted-foreground block text-xs', COMPACT_ONLY)}>
                    {showOwner ? `${row.owner.displayName} · ${category}` : category}
                  </span>
                </td>
                <td className={cn('px-3 py-3', WIDE_ONLY)}>{category}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap tabular-nums">
                  {formatAmount(row.total)}
                </td>
                <td className={cn('px-3 py-3 whitespace-nowrap', WIDE_ONLY)}>
                  <DocumentBadge status={row.documentStatus} missing={row.documentMissing} />
                </td>
                <td className="px-3 py-3">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <StatusBadge status={row.status} />
                    {/* #952 L2: an icon with its name, so the cell stays one line. */}
                    <UntraceableBadge traceable={row.traceablePayment} compact />
                  </span>
                  {row.documentMissing === true || row.documentStatus === 'awaited' ? (
                    <span className={cn('mt-1 block', COMPACT_ONLY)}>
                      <DocumentBadge status={row.documentStatus} missing={row.documentMissing} />
                    </span>
                  ) : null}
                </td>
                {showClass ? <ClassCell row={row} className={WIDE_ONLY} /> : null}
              </ClickableRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The phone's cards (decision 101's pattern, the board's «Telefono · la lista»): the supplier and
 * the total, the category and the day; the state only when it is not «Bozza», the document only
 * when it is not attached, so the card says what needs doing.
 */
function ExpenseCards({
  items,
  navigation,
  showOwner,
}: {
  readonly showOwner: boolean;
  readonly items: readonly ExpenseRow[];
  readonly navigation: CardNavigation;
}) {
  const { t, i18n } = useTranslation('expenses');
  return (
    <ul aria-label={t('list.title')} className="flex flex-col gap-2 @min-[620px]/shell:hidden">
      {items.map((row) => {
        const missing = row.documentMissing === true || row.documentStatus === 'awaited';
        return (
          <li key={row.id}>
            <Link
              to={`/expenses/${encodeURIComponent(row.id)}`}
              state={navigation}
              className="border-border bg-card flex items-start justify-between gap-3 rounded-xl border p-3"
            >
              <span className="flex min-w-0 flex-col gap-1">
                <b className="truncate">{expenseName(row, t)}</b>
                <small className="text-muted-foreground">
                  {categoryLabel(row.category, i18n.resolvedLanguage)} · {formatShortDate(row.date)}
                  {/* The desktop table has «Utente»: with everyone's records, the card names them. */}
                  {showOwner ? ` · ${row.owner.displayName}` : null}
                </small>
                {row.status !== 'draft' ? <StatusBadge status={row.status} /> : null}
                <UntraceableBadge traceable={row.traceablePayment} />
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1">
                <b className="tabular-nums">{formatAmount(row.total)}</b>
                {missing || row.documentStatus !== 'attached' ? (
                  <DocumentBadge status={row.documentStatus} missing={row.documentMissing} />
                ) : null}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * «Mostra: Le tue spese | Tutte le spese dell'organizzazione» (U3, #952 M2), for the roles that may
 * see both: it sets the `owner` filter to the caller or clears it. A segmented control on its own
 * row, so it does not read as one more view; the chosen half has the selection fill and a check
 * mark, recognisable without colour. Someone else's id (the «Utente» filter) leaves both unpressed.
 */
function ScopeSwitch({
  ownerId,
  owner,
  onChoose,
}: {
  readonly ownerId: string;
  readonly owner: string | null;
  readonly onChoose: (key: string, value: string | null) => void;
}) {
  const { t } = useTranslation('expenses-list');
  const labelId = useId();
  const choices = [
    { id: 'mine', label: t('scope.mine'), value: ownerId, pressed: owner === ownerId },
    { id: 'all', label: t('scope.all'), value: null, pressed: owner === null },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span id={labelId} className="text-muted-foreground text-sm">
        {t('scope.label')}
      </span>
      <div
        role="group"
        aria-labelledby={labelId}
        className="border-chip-border bg-card inline-flex rounded-md border p-0.5"
      >
        {choices.map((choice) => (
          <button
            key={choice.id}
            type="button"
            aria-pressed={choice.pressed}
            className={cn(
              'focus-visible:ring-ring inline-flex min-h-11 items-center gap-1.5 rounded-[5px] px-3 text-sm focus-visible:ring-2 focus-visible:outline-none @min-[620px]/shell:min-h-9',
              choice.pressed
                ? 'bg-selected text-selected-foreground font-semibold'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            onClick={() => onChoose('owner', choice.value)}
          >
            {choice.pressed ? <Check aria-hidden="true" className="size-4" /> : null}
            {choice.label}
          </button>
        ))}
      </div>
    </div>
  );
}
